/**
 * Submission keys for knowledge-check attempts.
 *
 * The server counts one attempt per distinct `Idempotency-Key`, so a retry after a lost response has to
 * present the key the first request used. The key therefore has to outlive the page: a dropped response is
 * exactly the moment a learner reloads, and a key held only in component state would be gone by the time
 * they resubmit, costing them an attempt they never knowingly used.
 */

/** The slice of the `Storage` interface used here; tests pass their own. */
export interface SubmissionStore {
  getItem(name: string): string | null;
  setItem(name: string, value: string): void;
  removeItem(name: string): void;
}

const PREFIX = "a123.check-submission.";

function memoryStore(): SubmissionStore {
  const held = new Map<string, string>();
  return {
    getItem: (name) => held.get(name) ?? null,
    setItem: (name, value) => void held.set(name, value),
    removeItem: (name) => void held.delete(name),
  };
}

let resolved: SubmissionStore | undefined;

/**
 * Session storage when it is actually writable, else an in-process map. Private browsing, blocked site
 * data, and a non-browser test runner all throw somewhere in here; the fallback keeps deduplication
 * working for the life of the page rather than failing the submission.
 */
function defaultStore(): SubmissionStore {
  if (resolved) return resolved;
  try {
    const probe = `${PREFIX}probe`;
    sessionStorage.setItem(probe, "1");
    sessionStorage.removeItem(probe);
    resolved = sessionStorage;
  } catch {
    resolved = memoryStore();
  }
  return resolved;
}

/** 32 hex characters: inside the contract's 8–128 `[A-Za-z0-9_-]`, and unlike `randomUUID` not secure-context-only. */
function randomKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Answers identify the submission; their insertion order does not. */
function signatureOf(answers: Record<string, string>): string {
  return JSON.stringify(Object.entries(answers).sort(([a], [b]) => a.localeCompare(b)));
}

/**
 * The key for submitting `answers` to `checkCode`: the key already held for this exact answer set, so a
 * retry is recognised, or a new one. Changing an answer makes it a different submission and a new attempt.
 */
export function submissionKeyFor(checkCode: string, answers: Record<string, string>, store: SubmissionStore = defaultStore()): string {
  const signature = signatureOf(answers);
  const name = PREFIX + checkCode;
  try {
    const held = store.getItem(name);
    if (held) {
      const parsed = JSON.parse(held) as { signature?: unknown; key?: unknown };
      if (parsed.signature === signature && typeof parsed.key === "string") return parsed.key;
    }
  } catch {
    // Unreadable or malformed: mint a new key rather than fail the submission.
  }
  const key = randomKey();
  try {
    store.setItem(name, JSON.stringify({ signature, key }));
  } catch {
    // Storage filled or became unavailable; the key is still used for this request.
  }
  return key;
}

/**
 * Drop the held key once the submission's outcome is known. Only an unknown outcome — a transport failure
 * or a 5xx — needs the key kept; after a result or an outright rejection, the next submission is a new one
 * and must be able to earn its own attempt.
 */
export function clearSubmissionKey(checkCode: string, store: SubmissionStore = defaultStore()): void {
  try {
    store.removeItem(PREFIX + checkCode);
  } catch {
    // Nothing retrievable was stored.
  }
}
